import React, { useState, useCallback } from 'react';
import { Link, Typography } from '@mui/material';
import Stack from '@mui/material/Stack';
import CardList from '../components/cards/card-list';
import { useChangePermissionsMutation, useGetGroupLayersQuery } from '../services/groups.service';
import { LayersBehavior } from '../models/group';
import type { GroupsLayer } from '../models/group';
import ConfirmModal from '../components/modals/confirm-modal';
import Search from '../components/search';
import linkTo from '../utils/linkTo';
import { searchSortHeaderStyles } from './__fixtures__';
import { listResourcesFallbackUIProps } from './group-fallback-ui';
import { OptionItem, EventData } from '../components/cards/card.types';

export interface Props {
  groupId: string;
}

const layersBehavior = new LayersBehavior();

export default function GroupsLayers({ groupId }: Props) {
  const [params, setParams] = useState({
    id: groupId,
    page: 1,
    per_page: 100,
    sort: 'name',
    search: '',
  });

  const { data: group, isFetching, isError } = useGetGroupLayersQuery({ ...params });
  const [layerId, setLayerId] = useState('');
  const [layers, setLayers] = useState<GroupsLayer[]>([]);
  const [changePermission] = useChangePermissionsMutation();
  const groupLayers = React.useMemo(
    () => [...layers, ...group?.layers ?? []], [group?.layers],
  );
  const showSearch = !!groupLayers.length || (!groupLayers.length && !!params.search.length);
  const isSearching = params.search !== '';
  const [removedLayerName, setRemovedLayerName] = useState<string | undefined>('');

  const handleRemove = () => {
    changePermission({
      type: 'group_layers',
      groupId,
      remove: [layerId],
      item: removedLayerName,
    });
    resetDeleteModal();
  };

  const handleFetchMore = useCallback(() => {
    if (group && params.page !== group.total_pages) {
      setParams({ ...params, page: params.page + 1 });
      setLayers(groupLayers);
    }
  }, [groupLayers]);

  const handleSearch = (search: string) => {
    setLayers([]);
    setParams({ ...params, page: 1, search });
  };
  const removeClick = (id: string, title: string) => {
    setLayerId(id);
    setRemovedLayerName(title);
  };
  const resetDeleteModal = () => setLayerId('');

  const { EmptyImage, emptyText, emptyTextHelper } = listResourcesFallbackUIProps({
    resource: 'layers',
    isSearching,
  });

  const onRemoveEvent = (_dataType: OptionItem, eventData: EventData) => {
    removeClick(eventData.id, eventData.title || '');
  };

  return (
    <>
      <ConfirmModal
        onPositive={handleRemove}
        onNegative={resetDeleteModal}
        positive="Remove"
        title="Remove Map Layer?"
        open={!!layerId}
        maxWidth="xs"
      >
        <Typography mb={2}>{layersBehavior.getDeleteText()}</Typography>
        <Link target="_blank" href={linkTo.groups.removingAccess} rel="noreferrer">
          Learn more
        </Link>
      </ConfirmModal>
      {showSearch && (
        <Stack sx={{ ...searchSortHeaderStyles }}>
          <Search
            setGlobalFilter={handleSearch}
            placeholder="Search name or description"
          />
        </Stack>
      )}
      <CardList
        hideAvatar
        rows={groupLayers}
        onEvent={onRemoveEvent}
        onFetchMore={handleFetchMore}
        isLoading={isFetching}
        error={isError && 'There was an error fetching members.'}
        EmptyImage={EmptyImage}
        emptyText={emptyText}
        emptyTextHelper={emptyTextHelper}
        actionItems={[OptionItem.Delete]}
      />
    </>
  );
}
